// src/pages/Orders.js
import React, { useEffect, useMemo, useState } from "react";
import OrdersTable from "../../src/components/orders/OrdersTable";
import OrdersFilters from "../../src/components/orders/OrdersFilters";
import Pagination from "../../src/components/orders/Pagination";
import useDebouncedValue from "../hooks/useDebounce";
import { fetchOrders } from "../services/orders.service";
import { sortParam, toTableRows } from "../services/orders.maps";
import { getUser } from "../lib/authToken";
import { DEV_USER } from "../config/localAuth";

// LocalStorage key for orders date range
const ORDERS_DATE_RANGE_KEY = "mlk.ordersDateRange";

// Helper to get default date range for page load
const getDefaultDateRange = () => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return {
    start_date: `${year}-${month}-${day}T00:00`,
    end_date: `${year}-${month}-${day}T${hours}:${minutes}`,
  };
};

// Helper to get saved date range from localStorage or default
const getSavedDateRange = () => {
  try {
    const saved = localStorage.getItem(ORDERS_DATE_RANGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate that we have valid date strings
      if (parsed.start_date && parsed.end_date) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn("Failed to parse saved date range:", e);
  }
  return getDefaultDateRange();
};

export default function Orders() {
  const user = getUser() || DEV_USER;

  const ORG_ID = user.restaurant_id;
  const LOCATION = user.location;

  // Initialize date range from localStorage or default
  const [dateRange, setDateRange] = useState(() => {
    return getSavedDateRange();
  });

  // Save date range to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(ORDERS_DATE_RANGE_KEY, JSON.stringify(dateRange));
  }, [dateRange]);

  const [search, setSearch] = useState("");
  const dq = useDebouncedValue(search, 300);

  const [sort, setSort] = useState({ field: "date", direction: "desc" });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [currency, setCurrency] = useState("INR");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Search filter
  const filteredRows = useMemo(() => {
    if (!dq) return rows;

    const query = dq.toLowerCase();

    return rows.filter(
      (row) =>
        row.id.toLowerCase().includes(query) ||
        (row.customer && row.customer.toLowerCase().includes(query))
    );
  }, [rows, dq]);

  // Date range change handler
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    setPage(1);
  };

  // Fetch orders
  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      setErr(null);

      try {
        const json = await fetchOrders({
          orgId: ORG_ID,
          location: LOCATION,
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          page,
          pageSize,
          sort: sortParam(sort.field, sort.direction),
          q: dq || "",
        });

        if (!cancelled) {
          setRows(toTableRows(json.items));

          setTotal(json?.meta?.totalItems ?? 0);
          setTotalPages(json?.meta?.totalPages ?? 0);

          setCurrency(json?.meta?.currency || "INR");
        }
      } catch (e) {
        if (!cancelled) setErr(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [dateRange, dq, sort, page, pageSize, LOCATION, ORG_ID]);

  // Clear filters
  const handleClear = () => {
    setSearch("");
    setDateRange(getDefaultDateRange());
    setPage(1);
  };

  return (
    <div className="page page--orders">

      <OrdersFilters
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
      />

      {err && (
        <div className="error-box">
          <div className="error-title">Couldn’t load orders</div>
          <div className="error-sub">{String(err.message || err)}</div>
        </div>
      )}

      <OrdersTable
        rows={filteredRows.map((order) => ({
          id: order.id,
          customer: order.customer,
          date: order.date,
          items: [],
          total: order.amount,
          status: order.status,
          order_type: order.order_type,
          table_number: order.table_number,
          amount: order.amount,
        }))}
        sort={sort}
        onSortChange={(next) => {
          setSort(next);
          setPage(1);
        }}
        currency={currency}
        onClear={handleClear}
        isLoading={loading}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />

    </div>
  );
}